import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent
} from "@mui/material";
import { PieChart, Pie, Cell } from "recharts";
import { McDrawer } from "@maersk-global/mds-react-wrapper/components-core/mc-drawer";
import { McButton } from "@maersk-global/mds-react-wrapper/components-core/mc-button";
import { McTooltip } from "@maersk-global/mds-react-wrapper/components-core/mc-tooltip";
import { McIcon } from "@maersk-global/mds-react-wrapper";

// Maersk font family
const maerskFontFamily = "Maersk Text, -apple-system, BlinkMacSystemFont, Microsoft JhengHei, Microsoft Yahei, 微软雅黑, STXihei, 华文细黑, sans-serif";

// ApplicationCard component for displaying applications grouped by ID
const ApplicationCard = ({ applicationId, applicationNames }) => {
  return (
    <Card 
      sx={{ 
        mb: 1, 
        border: '1px solid #d9d9d9', 
        boxShadow: 'none',
        borderRadius: '10px',
      }}
    >
      <CardContent sx={{ padding: '10px !important' }}>
        {/* Application ID Header - only show if not N/A */}
        {applicationId !== 'N/A' && (
          <Typography 
            variant="h6" 
            sx={{ 
              fontFamily: maerskFontFamily, 
              color: '#0073ab',
              fontSize: '0.9rem',
            }}
          >
            {applicationId}
          </Typography>
        )}

        {/* Application Names List */}
        <Box>
          <Typography 
            variant="body2" 
            sx={{ 
              fontFamily: maerskFontFamily, 
              color: '#666', 
              fontSize: '0.7rem',
            }}
          >
            {applicationNames}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

/**
 * OverviewCards component displays overview statistics cards with pie charts for the selected tab
 * 
 * @param {Array} applications - Array of applications for the current tab
 * @param {number} selectedTab - Currently selected tab index
 * @param {Object} applicationsData - Full applications data with counts
 * @param {Array} filteredCombinedApps - Pre-filtered combined applications (optional)
 * @returns {JSX.Element} The rendered overview cards with pie charts
 */
const OverviewCards = ({ selectedTab, applicationsData, filteredCombinedApps }) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerContent, setDrawerContent] = useState("");
  const [drawerHeading, setDrawerHeading] = useState("");

  // Colors for pie charts (matching LandingPage)
  const COLORS = ["#42B0D5", "#003E62", "#A1D1E8", "#E2F3FB"];

  // Calculate metrics using structured data from overviewCards
  const overviewData = applicationsData?.overviewCards || {};
  
  // Use filtered applications if available, otherwise use original data
  const useFilteredData = Array.isArray(filteredCombinedApps);
  
  let onboardedApps, notOnboardedApps, operationalGreen, operationalRed, channelRed, channelGreen;
  
  if (useFilteredData) {
    // Calculate from filtered combined apps
    const onboardedFilteredApps = filteredCombinedApps.filter(app => app.changeProcessType !== 'not_onboarded');
    const notOnboardedFilteredApps = filteredCombinedApps.filter(app => app.changeProcessType === 'not_onboarded');
    
    onboardedApps = onboardedFilteredApps.length;
    notOnboardedApps = notOnboardedFilteredApps.length;
    
    // Visibility health from filtered apps
    operationalGreen = onboardedFilteredApps.filter(app => app.visibilityHealth === true).length;
    operationalRed = filteredCombinedApps.filter(app => app.visibilityHealth === false || app.changeProcessType === 'not_onboarded').length;
    
    // Deployment channels from filtered apps (only onboarded apps have channels)
    channelRed = onboardedFilteredApps.filter(app => app.deploymentChannel === 'red').length;
    channelGreen = onboardedFilteredApps.filter(app => app.deploymentChannel === 'green').length;
  } else {
    // Get counts from structured data (original logic)
    onboardedApps = overviewData.allApplications?.uniqueOnboardedAppIds?.length || 0;
    notOnboardedApps = overviewData.allApplications?.notOnboardedApps?.length || 0;
    
    // Visibility health counts
    operationalGreen = overviewData.visibilityHealth?.changesVisible?.length || 0;
    operationalRed = overviewData.visibilityHealth?.changesNotVisible?.length || 0;

    // Deployment channel counts
    channelRed = overviewData.deploymentChannels?.redChannelApps?.length || 0;
    channelGreen = overviewData.deploymentChannels?.greenChannelApps?.length || 0;
  }
  
  const totalApps = onboardedApps + notOnboardedApps;

  // Handle drawer opening for different categories
  const handleDrawerOpen = (apps, title) => {
    // Group applications by ID for drawer display
    const groupApplicationsByID = (applications) => {
      const grouped = {};
      
      applications.forEach(app => {
        // Handle different data structures
        if (typeof app === 'string') {
          // For simple string arrays (legacy format)
          const appId = 'N/A';
          if (!grouped[appId]) {
            grouped[appId] = [];
          }
          grouped[appId].push(app);
        } else if (app && typeof app === 'object') {
          // For object format with code and name
          const appId = app.code || app.application_id || 'N/A';
          const appName = app.name || app.application_name || 'Unknown';
          if (!grouped[appId]) {
            grouped[appId] = [];
          }
          grouped[appId].push(appName);
        }
      });
      
      return grouped;
    };

    const groupedApps = groupApplicationsByID(apps);
    const groupedEntries = Object.entries(groupedApps);

    const content = (
      <Box sx={{ mt: 1, overflowY: 'auto', pr: 1 }}>
        <Typography 
          variant="body2" 
          sx={{ 
            mb: 2, 
            color: "#666", 
            fontSize: '1rem', 
            fontFamily: maerskFontFamily 
          }}
        >
          Total: {apps.length} applications
        </Typography>
        {groupedEntries.length > 0 ? (
          <Box>
            {groupedEntries.map(([applicationId, applicationNames], index) => (
              <ApplicationCard
                key={`app-card-${index}-${applicationId}`}
                applicationId={applicationId}
                applicationNames={applicationNames}
              />
            ))}
          </Box>
        ) : (
          <Typography sx={{ color: "#666", fontFamily: maerskFontFamily }}>
            No applications found.
          </Typography>
        )}
      </Box>
    );

    setDrawerHeading(title);
    setDrawerContent(content);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setDrawerContent("");
    setDrawerHeading("");
  };

  // Get applications by category using structured data or filtered data
  const getApplicationsByCategory = (category) => {
    // If filtered data is available, use it
    if (useFilteredData) {
      let result;
      switch (category) {
        case 'onboarded':
          result = filteredCombinedApps.filter(app => app.changeProcessType !== 'not_onboarded');
          break;
        case 'not_onboarded':
          result = filteredCombinedApps.filter(app => app.changeProcessType === 'not_onboarded');
          break;
        case 'operational_green':
          result = filteredCombinedApps.filter(app => app.visibilityHealth === true && app.changeProcessType !== 'not_onboarded');
          break;
        case 'operational_red':
          result = filteredCombinedApps.filter(app => app.visibilityHealth === false || app.changeProcessType === 'not_onboarded');
          break;
        case 'channel_green':
          result = filteredCombinedApps.filter(app => app.deploymentChannel === 'green' && app.changeProcessType !== 'not_onboarded');
          break;
        case 'channel_red':
          result = filteredCombinedApps.filter(app => app.deploymentChannel === 'red' && app.changeProcessType !== 'not_onboarded');
          break;
        default:
          result = [];
      }
      return result;
    }
    
    // Fallback to original logic if no filtered data
    const overviewData = applicationsData?.overviewCards || {};
    
    switch (category) {
      case 'onboarded':
        // For onboarded apps, we need to get the full app objects from classicChangeApps, rapidChangeApps, and classicRapidChangeApps
        const allOnboardedApps = [
          ...(applicationsData?.classicChangeApps || []),
          ...(applicationsData?.rapidChangeApps || []),
          ...(applicationsData?.classicRapidChangeApps || [])
        ];
        return allOnboardedApps;
      case 'not_onboarded':
        return overviewData.allApplications?.notOnboardedApps || [];
      case 'operational_green':
        // Find apps that have recent changes from all onboarded apps
        const allAppsForGreen = [
          ...(applicationsData?.classicChangeApps || []),
          ...(applicationsData?.rapidChangeApps || []),
          ...(applicationsData?.classicRapidChangeApps || [])
        ];
        const visibleAppIds = overviewData.visibilityHealth?.changesVisible || [];
        return allAppsForGreen.filter(app => visibleAppIds.includes(app.code));
      case 'operational_red':
        // Find apps that don't have recent changes from all onboarded apps plus not onboarded
        const allAppsForRed = [
          ...(applicationsData?.classicChangeApps || []),
          ...(applicationsData?.rapidChangeApps || []),
          ...(applicationsData?.classicRapidChangeApps || [])
        ];
        const notVisibleAppIds = overviewData.visibilityHealth?.changesNotVisible || [];
        const onboardedAppsWithoutChanges = allAppsForRed.filter(app => notVisibleAppIds.includes(app.code));
        const notOnboardedAppsForRed = overviewData.allApplications?.notOnboardedApps || [];
        return [...onboardedAppsWithoutChanges, ...notOnboardedAppsForRed];
      case 'channel_green':
        const allAppsForChannelGreen = [
          ...(applicationsData?.classicChangeApps || []),
          ...(applicationsData?.rapidChangeApps || []),
          ...(applicationsData?.classicRapidChangeApps || [])
        ];
        const greenChannelAppIds = overviewData.deploymentChannels?.greenChannelApps || [];
        return allAppsForChannelGreen.filter(app => greenChannelAppIds.includes(app.code));
      case 'channel_red':
        const allAppsForChannelRed = [
          ...(applicationsData?.classicChangeApps || []),
          ...(applicationsData?.rapidChangeApps || []),
          ...(applicationsData?.classicRapidChangeApps || [])
        ];
        const redChannelAppIds = overviewData.deploymentChannels?.redChannelApps || [];
        return allAppsForChannelRed.filter(app => redChannelAppIds.includes(app.code));
      default:
        return [];
    }
  };

  // Get tab-specific title prefix
  const getTabPrefix = () => {
    switch (selectedTab) {
      case 0: return "My Applications";
      case 1: return "My Platform Apps";
      case 2: return "My Portfolio Apps";
      default: return "My Applications";
    }
  };

  // Data for pie charts
  const applicationData = [
    {
      label: "Onboarded Apps",
      value: onboardedApps,
      category: 'onboarded',
      color: '#4caf50',
    },
    {
      label: "Not Onboarded Apps", 
      value: notOnboardedApps,
      category: 'not_onboarded',
      color: '#f44336'
    }
  ];

  const operationalData = [
    {
      label: "Apps having Changes Raised",
      value: operationalGreen,
      category: 'operational_green',
      color: '#4caf50'
    },
    {
      label: "Apps having No Changes Raised",
      value: operationalRed,
      category: 'operational_red',
      color: '#f44336'
    }
  ];

  // Special Deployment Channel Card component
  const DeploymentChannelCard = ({ title }) => (
    <Box style={{ width: '33.33%' }}>
      <Card 
        sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #d9d9d9', 
          boxShadow: 'none', 
          borderRadius: '10px'
        }}
      >
        <CardContent 
          sx={{ 
            textAlign: 'left',
            padding: 3,
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            justifyContent: 'center',
            alignItems: 'left'
          }}
        >
          <Typography 
            variant="subtitle1" 
            sx={{
              fontSize: '1.2rem',
              color: '#333',
              mb: 3,
              fontFamily: maerskFontFamily
            }}
          >
            {title}
          </Typography>
          
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "left",
              justifyContent: "center",
              flex: 1,
              pb: 1
            }}
          >
            {/* Big Number */}
            <Typography
              variant="h1"
              sx={{
                fontSize: "4rem",
                fontWeight: "bold",
                color: channelRed > 0 ? '#f44336': '#4caf50',
                cursor: channelRed > 0 ? "pointer" : "default",
                fontFamily: maerskFontFamily,
                "&:hover": {
                  opacity: channelRed > 0 ? 0.8 : 1
                }
              }}
              onClick={() => {
                if (channelRed > 0) {
                  const apps = getApplicationsByCategory('channel_red');
                  handleDrawerOpen(apps, 'Red Channel Applications');
                }
              }}
            >
              {channelRed}
            </Typography>
            
            {/* Description */}
            <Typography
              variant="body1"
              sx={{
                color: "#888888",
                fontSize: "1rem",
                fontWeight: "500",
                fontFamily: maerskFontFamily
              }}
            >
              Applications in Red Channel
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  // Chart component
  const ChartCard = ({ title, data, total, useCustomColors = false, showTooltip = false, tooltipText = "" }) => (
    <Box style={{ width: '33.33%' }}>
      <Card 
        sx={{ 
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid #d9d9d9', 
          boxShadow: 'none', 
          borderRadius: '10px'
        }}
      >
        <CardContent 
          sx={{ 
            textAlign: 'left',
            padding: 3,
            // '&:last-child': { pb: 2 },
            display: 'flex',
            flexDirection: 'column',
            flex: 1
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 1
            }}
          >
            <Typography 
              variant="subtitle1" 
              sx={{
                fontSize: '1.2rem',
                color: '#333',
                fontFamily: maerskFontFamily
              }}
            >
              {title}
            </Typography>
            {showTooltip && (
              <McTooltip opendelay={50} appearance="neutral-default" zindex={99999}>
                <McIcon icon="info-circle" slot="trigger" size="20"></McIcon>
                <span
                  style={{
                    maxWidth: "300px",
                    display: "flex"
                  }}
                >
                  {tooltipText}
                </span>
              </McTooltip>
            )}
          </Box>
          
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2
            }}
          >
            {/* Legend */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {data.map((entry, index) => (
                <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: useCustomColors ? entry.color : COLORS[index % COLORS.length]
                    }}
                  />
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      cursor: entry.value > 0 ? "pointer" : "default",
                      "&:hover": {
                        color: entry.value > 0 ? "#0073ab" : "#888888"
                      }
                    }}
                    onClick={() => {
                      if (entry.value > 0) {
                        const apps = getApplicationsByCategory(entry.category);
                        handleDrawerOpen(apps, `${entry.label}`);
                      }
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#888888",
                        cursor: entry.value > 0 ? "pointer" : "default",
                        fontFamily: maerskFontFamily,
                        "&:hover": {
                          color: entry.value > 0 ? "#0073ab" : "inherit"
                        }
                      }}
                    >
                      {entry.label}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: "bold",
                        cursor: entry.value > 0 ? "pointer" : "default",
                        fontFamily: maerskFontFamily,
                        "&:hover": {
                          color: entry.value > 0 ? "#0073ab" : "inherit"
                        }
                      }}
                    >
                      {entry.value}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>

            {/* Pie Chart */}
            <Box sx={{ position: "relative", width: 120, height: 120 }}>
              <PieChart width={120} height={120}>
                <Pie
                  data={data.map((item) => ({
                    ...item,
                    value: item.value || 0
                  }))}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={60}
                  dataKey="value"
                  startAngle={90}
                  endAngle={450}
                  isAnimationActive={false}
                >
                  {data.map((entry, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={useCustomColors ? entry.color : COLORS[index % COLORS.length]} 
                    />
                  ))}
                </Pie>
              </PieChart>
              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  fontSize: "20px",
                  fontWeight: "bold",
                  color: "#333"
                }}
              >
                {total}
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );

  return (
    <>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, justifyContent: 'center', alignItems: 'stretch' }}>
        <ChartCard 
          title={getTabPrefix()}
          data={applicationData}
          total={totalApps}
          useCustomColors={true}
        />
        <ChartCard 
          title="Visibility Health"
          data={operationalData}
          total={operationalGreen + operationalRed}
          useCustomColors={true}
          showTooltip={true}
          tooltipText="Based on changes raised in the last 3 months"
        />
        <DeploymentChannelCard 
          title="Deployment Channel"
        />
      </Box>

      {/* Drawer for showing application lists */}
      <McDrawer open={isDrawerOpen} closed={handleDrawerClose}>
        <span slot="heading" style={{ color: "#0073ab", fontFamily: maerskFontFamily }}>
          {drawerHeading}
        </span>
        <Box sx={{ mt: 1, overflowY: 'auto', pr: 1 }}>
          {drawerContent}
        </Box>
        <McButton slot="footer" dialogaction="close" onClick={handleDrawerClose}>
          Close
        </McButton>
      </McDrawer>
    </>
  );
};

export default OverviewCards;
